/**
 * QueryString Context Provider
 */

import * as AnalyticsActions from '../../redux/actions/analytics';

import { withRouter, WithRouter } from 'found';
import { isEqual, pick } from 'lodash';
import * as React from 'react';
import { connect } from 'react-redux';

import { getLicenseString, getLicenseUsagesFromQueryString } from '../../redux/helpers/license';
import { getLicenseComponents } from '../../redux/selectors/staticData';
import {
  addBudgetFilterToContext,
  addSearchFilterToContext,
  addSimilarityFilterToContext,
  addTaxonomyQueryToContext,
  addTempoFilterToContext,
  clearFiltersFromContext,
  clearSearchQueryFromContext,
  QueryStringContext,
  removeQsParamFromContext,
  removeSearchFilterFromContext,
  removeTaxonomyQueryFromContext,
  saveLicenseToContext,
  setSearchQueryInContext,
  setSearchSortOrderInContext,
} from './';
import { getQueryStringStateForRoute } from './helpers';
import { ContextAction, IQueryStringContext, QueryStringParam, SimpleContextAction } from './types';

interface IOwnStateProps {
  licenseComponents: GQL.ILicenseComponents;
}

interface IOwnDispatchProps {
  AnalyticsDispatch: {
    setLicense(licenseString: string): void;
    setSearch(state: QueryStringProviderState): void;
  };
}

type IOwnProps = WithRouter;

type QueryStringProviderProps = IOwnStateProps & IOwnDispatchProps & IOwnProps;
type QueryStringProviderState = Required<IQueryStringContext>;

class InternalQueryStringContextProvider extends React.Component<QueryStringProviderProps, QueryStringProviderState> {
  constructor(props) {
    super(props);

    this.state = {
      ...getQueryStringStateForRoute(this.props.match),

      addBudgetFilter: (budget) => {
        this.apply(addBudgetFilterToContext, budget);
      },
      addTempoFilter: (tempo) => {
        this.apply(addTempoFilterToContext, tempo);
      },
      addSearchFilter: (term, exclude) => {
        this.apply(addSearchFilterToContext, [term, !!exclude]);
      },
      clearFilters: () => {
        this.apply(clearFiltersFromContext);
      },
      removeFilter: (key) => {
        this.apply(removeQsParamFromContext, key);
      },
      removeBudgetFilter: () => this.state.removeFilter(QueryStringParam.Budget),
      removeLicense: () => this.state.removeFilter(QueryStringParam.License),
      removeSearchFilter: (term, exclude) => {
        this.apply(removeSearchFilterFromContext, [term, !!exclude]);
      },
      removeSimilarityFilter: () => this.state.removeFilter(QueryStringParam.Similarity),
      addTaxonomyQuery: (term) => {
        this.apply(addTaxonomyQueryToContext, term);
      },
      addSimilarityFilter: (trackId) => {
        this.apply(addSimilarityFilterToContext, trackId);
      },
      removeTaxonomyQuery: (term) => {
        this.apply(removeTaxonomyQueryFromContext, term);
      },
      removeTempoFilter: () => this.state.removeFilter(QueryStringParam.Tempo),
      saveLicense: (licenseParams) => {
        this.apply(saveLicenseToContext, licenseParams);
      },
      clearSearchQuery: () => {
        this.apply(clearSearchQueryFromContext);
      },
      setSearchQuery: (searchQuery) => {
        this.apply(setSearchQueryInContext, searchQuery);
      },
      setSearchSortOrder: (sortOrder) => {
        this.apply(setSearchSortOrderInContext, sortOrder);
      },
    };
  }

  componentDidUpdate(props: QueryStringProviderProps, prevState: QueryStringProviderState) {
    if (!isEqual(this.state.license_params, prevState.license_params)) {
      const usages = getLicenseUsagesFromQueryString({
        components: this.props.licenseComponents,
        params: this.state.license_params,
      });

      this.props.AnalyticsDispatch.setLicense(getLicenseString(usages));
    }

    if (this.searchHasChanged(prevState)) {
      this.props.AnalyticsDispatch.setSearch(this.state);
    }
  }

  apply<T>(action: ContextAction<T> | SimpleContextAction, arg?: T) {
    this.setState(() => {
      const { match, router } = this.props;
      const args: any[] = [this.state, { match, router }];

      if (arg) {
        args.unshift(arg);
      }

      return action.apply(null, args);
    });
  }

  render() {
    return (
      <QueryStringContext.Provider value={this.state}>
        {this.props.children}
      </QueryStringContext.Provider>
    );
  }

  searchHasChanged(prevState: QueryStringProviderState) {
    const keys: Array<keyof QueryStringProviderState> = ['filters', 'query', 'sort', 'license_params'];
    return !isEqual(pick(prevState, keys), pick(this.state, keys));
  }
}

const ConnectedQueryStringContextProvider =
  connect<IOwnStateProps, IOwnDispatchProps, IOwnProps, IStoreState>(
    (state) => ({
      licenseComponents: getLicenseComponents(state),
    }),
    (dispatch) => ({
      AnalyticsDispatch: {
        setLicense: (licenseString) => {
          dispatch(AnalyticsActions.setLicense(licenseString));
        },
        setSearch: ({ filters, query }) => {
          dispatch(AnalyticsActions.setSearch({ filters, query }));
        },
      },
    }),
  )(InternalQueryStringContextProvider);

export const QueryStringContextProvider = withRouter<{}>(ConnectedQueryStringContextProvider);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/contexts/queryString/provider.tsx